"use client";

import { Tabs as BaseTabs } from "@base-ui/react/tabs";
import type * as React from "react";

import { cn } from "@/lib/utils";

type Tabs2Variant = "primary" | "sub";
type Tabs2Size = "default" | "lg";

const Tabs2 = ({ className, ...props }: React.ComponentProps<typeof BaseTabs.Root>) => {
  return <BaseTabs.Root data-slot="tabs2" className={cn("flex flex-col gap-0", className)} {...props} />;
};

const TabsList2 = ({
  className,
  variant = "sub",
  ...props
}: React.ComponentProps<typeof BaseTabs.List> & { variant?: Tabs2Variant }) => {
  return (
    <BaseTabs.List
      data-slot="tabs-list2"
      data-variant={variant}
      className={cn(
        // Primary: borderless folder tabs with a thin gap; page background shows through.
        variant === "primary" && "grid w-full grid-cols-2 gap-1 bg-transparent p-0",
        variant === "sub" && "inline-flex h-auto w-full items-center gap-0 bg-transparent p-0",
        className,
      )}
      {...props}
    />
  );
};

const TabsTrigger2 = ({
  className,
  variant = "sub",
  size = "default",
  ...props
}: React.ComponentProps<typeof BaseTabs.Tab> & {
  variant?: Tabs2Variant;
  size?: Tabs2Size;
}) => {
  return (
    <BaseTabs.Tab
      data-slot="tabs-trigger2"
      data-variant={variant}
      data-size={size}
      className={cn(
        "inline-flex cursor-pointer items-center justify-center whitespace-nowrap transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        // Primary: slightly rounded tops; active = Gray/50, inactive = Gray/100.
        variant === "primary" &&
          cn(
            "rounded-t-[6px] border-0 bg-gray-100 text-muted-foreground shadow-none data-active:bg-gray-50 data-active:text-foreground",
            size === "default" && "px-4 py-2.5 text-sm font-medium",
            // Match Input `size="lg"` type size (18px).
            size === "lg" && "px-4 py-3 text-[18px] font-normal",
          ),
        variant === "sub" &&
          "relative h-auto rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground after:absolute after:inset-x-0 after:bottom-1 after:h-[1.5px] after:bg-transparent after:content-[''] data-active:after:bg-foreground data-active:text-foreground data-active:shadow-none",
        className,
      )}
      {...props}
    />
  );
};

const TabsContent2 = ({ className, ...props }: React.ComponentProps<typeof BaseTabs.Panel>) => {
  return <BaseTabs.Panel data-slot="tabs-content2" className={cn("flex-1 outline-none", className)} {...props} />;
};

export type { Tabs2Size, Tabs2Variant };
export { Tabs2, TabsContent2, TabsList2, TabsTrigger2 };
